import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

interface ReferralContextType {
  ref: string | null;
  getUrlWithRef: (url: string) => string;
}

const ReferralContext = createContext<ReferralContextType>({
  ref: null,
  getUrlWithRef: (url: string) => url,
});

export function ReferralProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const [ref, setRef] = useState<string | null>(null);

  useEffect(() => {
    // Check URL for ref parameter (CheckRefLink will handle validation and storage)
    const urlRef = searchParams.get('ref');
    
    if (urlRef) {
      setRef(urlRef);
    } else {
      // Check if we have a stored ref from CheckRefLink
      const storedRef = localStorage.getItem('ref');
      if (storedRef) {
        setRef(storedRef);
      }
    }
  }, [searchParams]);

  // Also listen for changes to localStorage (in case CheckRefLink updates it)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedRef = localStorage.getItem('ref');
      if (storedRef) {
        setRef(storedRef);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const getUrlWithRef = (url: string): string => {
    if (!ref) return url;

    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('ref', ref);
      return urlObj.toString();
    } catch {
      // If URL parsing fails, fall back to simple string concatenation
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}ref=${ref}`;
    }
  };

  return (
    <ReferralContext.Provider value={{ ref, getUrlWithRef }}>
      {children}
    </ReferralContext.Provider>
  );
}

export const useReferral = () => useContext(ReferralContext);